/**
 * Sale-compare table: SystemPanelHeader rows, column headers, line
 * items, Notes row (above Totals) and Totals row. The Payments row
 * used to live here; it now sits in a separate ``CompareSalesPaymentsBox``
 * directly below the table.
 *
 * All derivations come pre-computed from ``useCompareSalesData``; this
 * file owns rendering only.
 */
import React from 'react'
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { LightspeedIcon, XeroIcon } from '../../../../components/SystemIcons'
import {
  formatCurrency,
  formatPercentage,
} from '../../../../utils/inspectorFormatters'
import { expectedXeroStatus } from '../../../../utils/saleStatusDisplay'
import {
  COMPARE_COLORS,
  COMPARE_NUM_FONT_SX,
  renderDeltaChip,
} from '../../../../components/inspector/shared/compareSalesPalette'
import { poolwerxColors } from '../../../../theme/colors'
import SystemPanelHeader from '../../../../components/inspector/shared/SystemPanelHeader'
import {
  renderAmountCell,
  renderProductCell,
} from '../../../../components/inspector/shared/SaleLineItemCells'
import type { CompareSalesData } from './useCompareSalesData'
import CompareSalesFooterRow from './CompareSalesFooter'

const COLUMN_HEADER_BG = '#ECEFF1'

// "(ex. tax)" / "(inc. tax)" qualifier under the Total column headings.
const TOTAL_QUALIFIER_SX = {
  display: 'block',
  fontWeight: 400,
  fontSize: '0.7rem',
  color: 'text.secondary',
  lineHeight: 1.1,
} as const

const renderTaxTooltip = (
  label?: string | null,
  rate?: number | null,
): string => {
  if (rate != null) return `Tax - ${formatPercentage(rate, 2, true)}`
  if (label) return label
  return 'No tax info'
}

// Centered notice for the half of the compare view whose counterpart
// is absent. Renders inside a single colSpan=5 / rowSpan=<bodyRows>
// cell so the surviving half keeps its full table layout.
const EmptySidePanel: React.FC<{
  system: 'lightspeed' | 'xero'
  counterpartReference?: string
}> = ({ system, counterpartReference }) => {
  const icon =
    system === 'lightspeed' ? (
      <LightspeedIcon size={48} />
    ) : (
      <XeroIcon size={48} />
    )
  const counterpartLabel = counterpartReference
    ? system === 'lightspeed'
      ? `Xero ${counterpartReference}`
      : `LS #${counterpartReference}`
    : null
  const message =
    system === 'lightspeed' ? 'No Lightspeed sale' : 'No Xero invoice'
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={1.25}
      sx={{ py: 6 }}
    >
      <Box sx={{ opacity: 0.5 }}>{icon}</Box>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ fontWeight: 600 }}
      >
        {message}
      </Typography>
      {counterpartLabel && (
        <Typography variant="caption" color="text.disabled">
          for {counterpartLabel}
        </Typography>
      )}
    </Stack>
  )
}

interface CompareSalesTableProps {
  data: CompareSalesData
  showDiffsOnly: boolean
  lsNotesExpanded: boolean
  xeroNotesExpanded: boolean
  onToggleLsNotes: () => void
  onToggleXeroNotes: () => void
  invoiceNumber?: string
  xeroReference?: string
  xeroInvoiceNumber?: string
  lightspeedSaleLink?: string
  xeroInvoiceLink?: string
}

const CompareSalesTable: React.FC<CompareSalesTableProps> = ({
  data,
  showDiffsOnly,
  lsNotesExpanded,
  xeroNotesExpanded,
  onToggleLsNotes,
  onToggleXeroNotes,
  invoiceNumber,
  xeroReference,
  xeroInvoiceNumber,
  lightspeedSaleLink,
  xeroInvoiceLink,
}) => {
  const {
    lightspeed,
    xero,
    xeroMissing,
    saleLines,
    clientMismatch,
    dateMismatch,
    survivingBodyRowSpan,
  } = data

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      // No Paper-level horizontal borders so the centre gutter column
      // stays uncrossed. The footer row paints its own bottom border
      // on the LS and Xero halves only.
      sx={{ borderTop: 0, borderBottom: 0 }}
    >
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <colgroup>
          {/* Lightspeed columns - Item flexes, numeric columns fixed */}
          <col />
          <col style={{ width: '70px' }} />
          <col style={{ width: '105px' }} />
          <col style={{ width: '95px' }} />
          <col style={{ width: '105px' }} />
          {/* Middle status column */}
          <col style={{ width: '48px' }} />
          {/* Xero columns - mirror LS widths */}
          <col />
          <col style={{ width: '70px' }} />
          <col style={{ width: '105px' }} />
          <col style={{ width: '95px' }} />
          <col style={{ width: '105px' }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell
              colSpan={5}
              sx={{
                backgroundColor: '#FFFFFF',
                borderTop: `2px solid ${poolwerxColors.teal}`,
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                py: 1.75,
              }}
            >
              <SystemPanelHeader
                icon={<LightspeedIcon size={40} />}
                systemLabel="Lightspeed"
                system="lightspeed"
                reference={invoiceNumber}
                link={lightspeedSaleLink}
                client={lightspeed.customer_name}
                date={lightspeed.sale_date}
                status={lightspeed.status}
                clientMismatch={clientMismatch}
                dateMismatch={dateMismatch}
              />
            </TableCell>
            <TableCell
              sx={{
                width: 48,
                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                borderTop: '0 !important',
                borderBottom: '0 !important',
                backgroundColor: '#FFFFFF',
                textAlign: 'center',
                color: 'text.disabled',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              vs
            </TableCell>
            <TableCell
              colSpan={5}
              sx={{
                backgroundColor: '#FFFFFF',
                borderTop: `2px solid ${poolwerxColors.teal}`,
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                py: 1.75,
              }}
            >
              <SystemPanelHeader
                icon={<XeroIcon size={40} />}
                systemLabel={
                  xero.kind === 'credit_note' ? 'Xero credit note' : 'Xero'
                }
                system="xero"
                reference={
                  xeroMissing ? undefined : xeroReference || xeroInvoiceNumber
                }
                link={xeroMissing ? undefined : xeroInvoiceLink}
                client={xeroMissing ? null : xero.contact_name}
                date={xeroMissing ? null : xero.invoice_date}
                status={xeroMissing ? null : xero.status}
                expectedStatus={
                  xeroMissing ? null : expectedXeroStatus(lightspeed.status)
                }
                clientMismatch={xeroMissing ? false : clientMismatch}
                dateMismatch={xeroMissing ? false : dateMismatch}
              />
            </TableCell>
          </TableRow>
          <TableRow sx={{ backgroundColor: COLUMN_HEADER_BG }}>
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Total
              <Box component="span" sx={TOTAL_QUALIFIER_SX}>
                (ex. tax)
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Tax
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
              }}
              align="right"
            >
              Total
              <Box component="span" sx={TOTAL_QUALIFIER_SX}>
                (inc. tax)
              </Box>
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                borderTop: '0 !important',
                borderBottom: '0 !important',
                backgroundColor: '#FFFFFF',
              }}
            />
            <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="center">
              Qty
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Total
              <Box component="span" sx={TOTAL_QUALIFIER_SX}>
                (ex. tax)
              </Box>
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Tax
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }} align="right">
              Total
              <Box component="span" sx={TOTAL_QUALIFIER_SX}>
                (inc. tax)
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {saleLines.length === 0 && (
            <TableRow>
              <TableCell colSpan={11} sx={{ py: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {showDiffsOnly
                    ? 'No differences - all rows match'
                    : 'No line items'}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {saleLines.map((row, index) => {
            const {
              lsItem,
              xeroItem,
              isError,
              isWarning,
              isWarningOnly,
              isUnmatchedLs,
              isUnmatchedXero,
              priceMismatch,
              qtyMismatch,
              descriptionMismatch,
            } = row
            const lsIsSynth = !!lsItem?.is_synthetic_note

            // Whole-side red is confined to unmatched-row case (one
            // side has the line, the other is empty + error).
            // Matched-pair rows colour cells individually based on
            // their own mismatch flag.
            const missingRowError =
              (!!isUnmatchedLs || !!isUnmatchedXero) && isError
            const lsUnmatchedRed = !!isUnmatchedLs && isError
            const xeroUnmatchedRed = !!isUnmatchedXero && isError

            const lsProductRed = lsUnmatchedRed
            const xeroProductRed = xeroUnmatchedRed
            const lsQtyRed = lsUnmatchedRed || (qtyMismatch && isError)
            const xeroQtyRed = xeroUnmatchedRed || (qtyMismatch && isError)
            const lsAmountRed = lsUnmatchedRed || priceMismatch
            const xeroAmountRed = xeroUnmatchedRed || priceMismatch

            const bothPresent = !!lsItem && !!xeroItem
            const lineQtyDelta = bothPresent
              ? (xeroItem!.quantity ?? 0) - (lsItem!.quantity ?? 0)
              : null
            const lineExDelta = bothPresent
              ? (xeroItem!.amount_ex_tax ?? 0) - (lsItem!.amount_ex_tax ?? 0)
              : null
            const lineTaxDelta = bothPresent
              ? (xeroItem!.tax ?? 0) - (lsItem!.tax ?? 0)
              : null
            const lineIncDelta = bothPresent
              ? (xeroItem!.amount_inc_tax ?? 0) -
                (lsItem!.amount_inc_tax ?? 0)
              : null

            const rowKey = `${lsItem?.id ?? 'x'}__${xeroItem?.id ?? 'x'}__${index}`
            return (
              <TableRow
                key={rowKey}
                hover
                sx={{
                  '& td': { verticalAlign: 'top' },
                  ...(missingRowError && {
                    backgroundColor: COMPARE_COLORS.mismatchBg,
                  }),
                  '&:hover td': {
                    backgroundColor: missingRowError
                      ? 'rgba(236, 28, 90, 0.16)'
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <TableCell>
                  {renderProductCell(
                    lsItem,
                    !!descriptionMismatch,
                    lsProductRed,
                  )}
                </TableCell>
                <TableCell align="center">
                  {lsItem ? (
                    <Typography
                      variant="body2"
                      sx={{
                        fontVariantNumeric: 'tabular-nums',
                        color: lsQtyRed
                          ? COMPARE_COLORS.mismatchBorder
                          : qtyMismatch
                            ? COMPARE_COLORS.warningText
                            : undefined,
                        fontWeight:
                          lsQtyRed || qtyMismatch ? 600 : undefined,
                      }}
                    >
                      {lsIsSynth ? '-' : lsItem.quantity}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      sx={{ textAlign: 'right' }}
                    >
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {renderAmountCell(
                    lsItem?.amount_ex_tax,
                    lsAmountRed,
                    lsIsSynth,
                  )}
                </TableCell>
                <TableCell align="right">
                  {lsItem && !lsIsSynth ? (
                    <Tooltip
                      title={renderTaxTooltip(
                        lsItem.tax_label,
                        lsItem.tax_rate,
                      )}
                      arrow
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          textAlign: 'right',
                          cursor: 'help',
                          color: lsAmountRed
                            ? COMPARE_COLORS.mismatchBorder
                            : undefined,
                          fontWeight: lsAmountRed ? 600 : undefined,
                        }}
                      >
                        {formatCurrency(lsItem.tax)}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.disabled"
                      sx={{ textAlign: 'right' }}
                    >
                      -
                    </Typography>
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ borderRight: '1px solid rgba(0, 0, 0, 0.12)' }}
                >
                  {renderAmountCell(
                    lsItem?.amount_inc_tax,
                    lsAmountRed,
                    lsIsSynth,
                  )}
                </TableCell>

                <TableCell
                  align="center"
                  sx={{
                    width: 48,
                    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                    borderTop: '0 !important',
                    borderBottom: '0 !important',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  {isError ? (
                    <Tooltip
                      title={
                        priceMismatch
                          ? 'Price difference'
                          : isUnmatchedLs
                            ? 'Missing in Xero'
                            : isUnmatchedXero
                              ? 'Missing in Lightspeed'
                              : 'Difference'
                      }
                      arrow
                    >
                      <ErrorOutlineIcon
                        sx={{
                          fontSize: 20,
                          color: COMPARE_COLORS.mismatchBorder,
                        }}
                      />
                    </Tooltip>
                  ) : isWarning ? (
                    <Tooltip
                      title={
                        isWarningOnly
                          ? '$0 / rounding / note'
                          : qtyMismatch && descriptionMismatch
                            ? 'Qty & description difference'
                            : qtyMismatch
                              ? 'Qty difference'
                              : 'Description difference'
                      }
                      arrow
                    >
                      <WarningAmberIcon
                        sx={{ fontSize: 20, color: 'warning.main' }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Matched" arrow>
                      <CheckCircleOutlineIcon
                        sx={{ fontSize: 20, color: 'success.main' }}
                      />
                    </Tooltip>
                  )}
                </TableCell>

                {xeroMissing ? (
                  index === 0 ? (
                    <TableCell
                      colSpan={5}
                      rowSpan={survivingBodyRowSpan}
                      sx={{
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        backgroundColor: 'grey.50',
                        borderLeft: '1px solid rgba(0, 0, 0, 0.06)',
                      }}
                    >
                      <EmptySidePanel
                        system="xero"
                        counterpartReference={invoiceNumber}
                      />
                    </TableCell>
                  ) : null
                ) : (
                  <>
                    <TableCell>
                      {renderProductCell(
                        xeroItem,
                        !!descriptionMismatch,
                        xeroProductRed,
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {xeroItem ? (
                        <Stack spacing={0.375} alignItems="center">
                          <Typography
                            variant="body2"
                            sx={{
                              ...COMPARE_NUM_FONT_SX,
                              color: xeroQtyRed
                                ? COMPARE_COLORS.mismatchBorder
                                : qtyMismatch
                                  ? COMPARE_COLORS.warningText
                                  : undefined,
                              fontWeight:
                                xeroQtyRed || qtyMismatch ? 600 : undefined,
                            }}
                          >
                            {xeroItem.quantity || '-'}
                          </Typography>
                          {qtyMismatch &&
                            lineQtyDelta != null &&
                            renderDeltaChip(lineQtyDelta, false)}
                        </Stack>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.disabled"
                          sx={{ textAlign: 'right' }}
                        >
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {renderAmountCell(
                        xeroItem?.amount_ex_tax,
                        xeroAmountRed,
                        !xeroItem ||
                          (xeroItem.amount_ex_tax === 0 && !xeroItem.tax),
                        priceMismatch ? lineExDelta : undefined,
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {xeroItem && (xeroItem.tax || xeroItem.tax_label) ? (
                        <Stack spacing={0.375} alignItems="flex-end">
                          <Tooltip
                            title={renderTaxTooltip(
                              xeroItem.tax_label,
                              xeroItem.tax_rate,
                            )}
                            arrow
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                ...COMPARE_NUM_FONT_SX,
                                textAlign: 'right',
                                cursor: 'help',
                                color: xeroAmountRed
                                  ? COMPARE_COLORS.mismatchBorder
                                  : undefined,
                                fontWeight: xeroAmountRed ? 600 : undefined,
                              }}
                            >
                              {formatCurrency(xeroItem.tax)}
                            </Typography>
                          </Tooltip>
                          {priceMismatch &&
                            lineTaxDelta != null &&
                            renderDeltaChip(lineTaxDelta, true)}
                        </Stack>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.disabled"
                          sx={{ textAlign: 'right' }}
                        >
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {renderAmountCell(
                        xeroItem?.amount_inc_tax,
                        xeroAmountRed,
                        !xeroItem ||
                          (xeroItem.amount_inc_tax === 0 && !xeroItem.tax),
                        priceMismatch ? lineIncDelta : undefined,
                      )}
                    </TableCell>
                  </>
                )}
              </TableRow>
            )
          })}
          {/* Invoice-style settlement footer row. Sits directly below
            * the last line item and replaces both the old in-table
            * Totals row AND the standalone Notes row / Payments box.
            * Each side renders notes + Updated date on the lower-left
            * and a compact invoice summary (Subtotal / Sale total /
            * Less rows / Amount due) on the right. */}
          <CompareSalesFooterRow
            data={data}
            lsNotesExpanded={lsNotesExpanded}
            xeroNotesExpanded={xeroNotesExpanded}
            onToggleLsNotes={onToggleLsNotes}
            onToggleXeroNotes={onToggleXeroNotes}
          />
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default CompareSalesTable
